const stringMatches = (pattern) => (value) => typeof value === 'string' && pattern.test(value);
const date = /^\d{4}-\d{2}-\d{2}$/;
const timestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
const validators = {
  boolean: (value) => typeof value === 'boolean',
  text: (value) => typeof value === 'string',
  relation: (value) => typeof value === 'string' && value.length > 0,
  integer: stringMatches(/^-?(0|[1-9][0-9]*)$/),
  decimal: stringMatches(/^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/),
  enum: (value, field) => field.enumValues?.includes(value) === true,
  date: (value) =>
    stringMatches(date)(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value,
  timestamp: (value) =>
    stringMatches(timestamp)(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 19) === value.slice(0, 19),
};

export function validateFieldValue(value, field, complexValidators = {}) {
  if (value === null) return field.nullable === true;
  const validate = complexValidators[field.type] ?? validators[field.type];
  return !!validate?.(value, field);
}

/** Postgres stores no NUL character (U+0000) in text or JSON, and no unpaired
 * UTF-16 surrogate (half of a character, as text cut inside an emoji leaves):
 * JSON refuses one and text would store U+FFFD in its place. A string that
 * holds either is not a value an App table can store: every App write, id,
 * default and public form refuses it as an invalid value, never as an outage.
 * A loop over code units, as this also runs in the App's browser, where
 * String.prototype.isWellFormed may not exist. */
export function storableString(value) {
  for (let index = 0; index < value.length; index++) {
    const unit = value.charCodeAt(index);
    if (unit === 0 || (unit >= 0xdc00 && unit <= 0xdfff)) return false;
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
      index++;
    }
  }
  return true;
}

/** Why such a string is refused, in the words App errors use. */
export const UNSTORABLE_TEXT =
  'text cannot contain the NUL character (U+0000) or half of a character (an unpaired surrogate)';

/** Every string in a JSON value, object keys included, is storable. */
export function storableJson(value) {
  if (typeof value === 'string') return storableString(value);
  if (value === null || typeof value !== 'object') return true;
  return Object.entries(value).every(([key, item]) => storableString(key) && storableJson(item));
}

/** The path of every string in a JSON value that is not storable (an object
 * key's path ends in that key). */
export function unstorablePaths(value, path = []) {
  if (typeof value === 'string') return storableString(value) ? [] : [path];
  if (value === null || typeof value !== 'object') return [];
  return Object.entries(value).flatMap(([key, item]) =>
    storableString(key)
      ? unstorablePaths(item, [...path, Array.isArray(value) ? Number(key) : key])
      : [[...path, key]],
  );
}
